import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiAlertCircle,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiFileText,
  FiLoader,
  FiSend,
  FiUser,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import HrmsFilters from '../../components/hrms/HrmsFilterPanel';
import SearchableSelect from '../../components/reports/SearchableSelect';
import { filterEmployeesForLeave } from './leaveEmployeeFilterUtils';

const steps = ["Employee", "Leave Type", "Dates", "Review"];

const initialForm = {
  employeeId: "",
  leaveTypeId: "",
  startDate: "",
  endDate: "",
  dayUnit: "FULL_DAY",
  reason: "",
};

export default function ApplyLeave() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [currentStep, setCurrentStep] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeFilters, setEmployeeFilters] = useState({});
  const filteredEmployees = useMemo(() => filterEmployeesForLeave(employees,employeeFilters,employeeSearch), [employees,employeeFilters,employeeSearch]);
  useEffect(() => {
    if (formData.employeeId && !filteredEmployees.some(employee => employee.id === formData.employeeId)) {
      setFormData(current => ({ ...current, employeeId:'' }));
    }
  }, [filteredEmployees,formData.employeeId]);

  useEffect(() => {
    async function loadFormOptions() {
      setIsLoading(true);
      setMessage("");

      try {
        const [employeeResponse, leaveTypeResponse] = await Promise.all([
          apiRequest("/leave/employees", { accessToken }),
          apiRequest("/leave/types?activeOnly=true", { accessToken }),
        ]);

        const activeEmployees = (Array.isArray(employeeResponse)
          ? employeeResponse
          : []
        ).filter((employee) => employee.status === "ACTIVE");

        setEmployees(activeEmployees);
        setLeaveTypes(
          Array.isArray(leaveTypeResponse) ? leaveTypeResponse : [],
        );
      } catch (error) {
        setMessage(
          error.message || "Could not load employees and leave types.",
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadFormOptions();
  }, [accessToken]);

  const selectedEmployee = useMemo(
    () =>
      employees.find((employee) => employee.id === formData.employeeId) ||
      null,
    [employees, formData.employeeId],
  );

  const selectedLeaveType = useMemo(
    () =>
      leaveTypes.find((leaveType) => leaveType.id === formData.leaveTypeId) ||
      null,
    [leaveTypes, formData.leaveTypeId],
  );

  const totalDays = useMemo(() => {
    if (!formData.startDate || !formData.endDate) {
      return 0;
    }

    const startDate = new Date(`${formData.startDate}T00:00:00`);
    const endDate = new Date(`${formData.endDate}T00:00:00`);

    if (endDate < startDate) {
      return 0;
    }

    if (formData.dayUnit === "HALF_DAY") {
      return startDate.getTime() === endDate.getTime() ? 0.5 : 0;
    }

    return (
      Math.floor(
        (endDate.getTime() - startDate.getTime()) /
          (24 * 60 * 60 * 1000),
      ) + 1
    );
  }, [formData.startDate, formData.endDate, formData.dayUnit]);

  const updateField = (name, value) => {
    setFormData((current) => ({
      ...current,
      [name]: value,
      ...(name === "dayUnit" && value === "HALF_DAY"
        ? { endDate: current.startDate }
        : {}),
    }));

    setErrors((current) => ({
      ...current,
      [name]: "",
    }));
  };

  const validateStep = () => {
    const nextErrors = {};

    if (currentStep === 0 && !formData.employeeId) {
      nextErrors.employeeId = "Please select an employee.";
    }

    if (currentStep === 1 && !formData.leaveTypeId) {
      nextErrors.leaveTypeId = "Please select a leave type.";
    }

    if (currentStep === 2) {
      if (!formData.startDate) {
        nextErrors.startDate = "Please select a start date.";
      }

      if (!formData.endDate) {
        nextErrors.endDate = "Please select an end date.";
      }

      if (formData.startDate && formData.endDate && totalDays <= 0) {
        nextErrors.endDate =
          formData.dayUnit === "HALF_DAY"
            ? "Half-day leave must begin and end on the same date."
            : "End date cannot be before the start date.";
      }

      if (!formData.reason.trim()) {
        nextErrors.reason = "Please enter a reason.";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep((current) => current + 1);
    }
  };

  const previousStep = () => {
    setCurrentStep((current) => Math.max(0, current - 1));
  };

  const submitDirectLeave = async () => {
    if (!validateStep()) {
      setCurrentStep(2);
      return;
    }

    setIsSubmitting(true);
    setMessage("");

    try {
      await apiRequest("/leave/records", {
        method: "POST",
        accessToken,
        body: {
          employeeId: formData.employeeId,
          leaveTypeId: formData.leaveTypeId,
          startDate: formData.startDate,
          endDate: formData.endDate,
          dayUnit: formData.dayUnit,
          reason: formData.reason.trim(),
        },
      });

      navigate("/admindashboard/leave", {
        replace: true,
        state: {
          successMessage: "Employee leave was recorded and approved.",
        },
      });
    } catch (error) {
      const backendMessage = Array.isArray(error.details?.message)
        ? error.details.message.join(", ")
        : error.message;

      setMessage(backendMessage || "Could not record employee leave.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[420px] flex items-center justify-center text-primary-700">
        <FiLoader className="animate-spin mr-3" size={24} />
        Loading leave form...
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <button
            onClick={() => navigate("/admindashboard/leave")}
            className="mb-4 text-sm font-medium text-gray-500 hover:text-primary-700"
          >
            ← Back to Leave Management
          </button>

          <h1 className="text-3xl font-bold text-gray-900">
            Record Employee Leave
          </h1>

          <p className="mt-2 text-gray-500">
            HR can record approved leave directly without an approval request.
          </p>
        </div>

        <div className="rounded-2xl bg-green-50 border border-green-100 px-4 py-3 text-sm text-green-700">
          Directly recorded leave is approved immediately.
        </div>
      </div>

      {message && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-700 flex gap-3">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{message}</p>
        </div>
      )}

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {steps.map((step, index) => (
            <div key={step} className="flex items-center gap-3">
              <div
                className={`h-12 w-12 shrink-0 rounded-2xl flex items-center justify-center font-semibold ${
                  currentStep > index
                    ? "bg-green-100 text-green-600"
                    : currentStep === index
                      ? "bg-primary-700 text-white"
                      : "bg-gray-100 text-gray-400"
                }`}
              >
                {currentStep > index ? <FiCheck /> : index + 1}
              </div>

              <div>
                <p className="font-medium text-gray-900">{step}</p>
                <p className="text-xs text-gray-500">Step {index + 1}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
        {currentStep === 0 && (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold">Select Employee</h2>
              <p className="mt-2 text-gray-500">
                Select the employee whose leave should be recorded.
              </p>
            </div>

            <div className="max-w-3xl">
              <div className="mb-5"><HrmsFilters records={employees} value={employeeFilters} fields={['branch','department']} onChange={next => { setEmployeeFilters(next); if (!Object.keys(next).length) setEmployeeSearch(''); }} /></div>
              <label htmlFor="record-leave-employee-search" className="mb-2 block font-medium text-gray-700">Search employees</label>
              <input id="record-leave-employee-search" type="search" value={employeeSearch} onChange={event => setEmployeeSearch(event.target.value)} placeholder="Search name, nickname or employee code" className="mb-4 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100" />
              <label className="block mb-2 font-medium text-gray-700">
                Employee *
              </label>

              <SearchableSelect
                value={formData.employeeId}
                onChange={value => updateField('employeeId',value)}
                placeholder="Select employee"
                disabled={isLoading}
                options={filteredEmployees.map(employee => ({value:employee.id,label:`${employee.fullName} · ${employee.employeeCode}`}))}
              />
              <p className="mt-2 text-sm text-gray-500">{filteredEmployees.length} matching employee(s){!filteredEmployees.length && ' — clear the search or reset filters to see employees.'}</p>

              {errors.employeeId && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.employeeId}
                </p>
              )}
            </div>

            {selectedEmployee && (
              <div className="mt-8 rounded-2xl bg-primary-50 p-6 flex items-center gap-4">
                <div className="h-14 w-14 rounded-2xl bg-primary-100 text-primary-700 flex items-center justify-center text-lg font-bold">
                  {selectedEmployee.fullName
                    .split(" ")
                    .map((word) => word[0])
                    .join("")
                    .slice(0, 2)}
                </div>

                <div>
                  <h3 className="font-bold text-gray-900">
                    {selectedEmployee.fullName}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {selectedEmployee.employeeCode}
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {currentStep === 1 && (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold">Select Leave Type</h2>
              <p className="mt-2 text-gray-500">
                Choose the type of leave to record.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {leaveTypes.map((leaveType) => (
                <button
                  key={leaveType.id}
                  type="button"
                  onClick={() => updateField("leaveTypeId", leaveType.id)}
                  className={`p-6 rounded-2xl border-2 text-left transition-all ${
                    formData.leaveTypeId === leaveType.id
                      ? "border-primary-700 bg-primary-50"
                      : "border-gray-200 hover:border-primary-400"
                  }`}
                >
                  <FiCalendar className="mb-4 text-primary-700" size={24} />

                  <h3 className="font-semibold text-gray-900">
                    {leaveType.name}
                  </h3>

                  <p className="mt-2 text-sm text-gray-500">
                    {leaveType.code}
                    {leaveType.isPaid ? " · Paid leave" : " · Unpaid leave"}
                  </p>
                </button>
              ))}
            </div>

            {!leaveTypes.length && (
              <p className="text-gray-500">
                No active leave types are available.
              </p>
            )}

            {errors.leaveTypeId && (
              <p className="mt-4 text-sm text-red-600">
                {errors.leaveTypeId}
              </p>
            )}
          </>
        )}

        {currentStep === 2 && (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold">Dates and Reason</h2>
              <p className="mt-2 text-gray-500">
                This leave will be approved as soon as it is saved.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block mb-2 font-medium">Leave Duration</label>

                <select
                  value={formData.dayUnit}
                  onChange={(event) =>
                    updateField("dayUnit", event.target.value)
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary-700"
                >
                  <option value="FULL_DAY">Full Day</option>
                  <option value="HALF_DAY">Half Day</option>
                </select>
              </div>

              <div className="rounded-2xl bg-primary-50 px-6 py-4">
                <p className="text-sm text-gray-500">Total Leave Days</p>
                <p className="mt-1 text-3xl font-bold text-primary-800">
                  {totalDays}
                </p>
              </div>

              <div>
                <label className="block mb-2 font-medium">Start Date *</label>

                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(event) => {
                    updateField("startDate", event.target.value);

                    if (formData.dayUnit === "HALF_DAY") {
                      updateField("endDate", event.target.value);
                    }
                  }}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-700"
                />

                {errors.startDate && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.startDate}
                  </p>
                )}
              </div>

              <div>
                <label className="block mb-2 font-medium">End Date *</label>

                <input
                  type="date"
                  min={formData.startDate || undefined}
                  disabled={formData.dayUnit === "HALF_DAY"}
                  value={formData.endDate}
                  onChange={(event) =>
                    updateField("endDate", event.target.value)
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-700 disabled:bg-gray-50"
                />

                {errors.endDate && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.endDate}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <label className="block mb-2 font-medium">Reason *</label>

              <textarea
                rows={5}
                value={formData.reason}
                onChange={(event) => updateField("reason", event.target.value)}
                placeholder="Explain why this leave is being recorded..."
                className="w-full px-4 py-3 rounded-xl border border-gray-200 resize-none focus:outline-none focus:ring-2 focus:ring-primary-700"
              />

              {errors.reason && (
                <p className="mt-2 text-sm text-red-600">{errors.reason}</p>
              )}
            </div>
          </>
        )}

        {currentStep === 3 && (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold">Review Leave Record</h2>
              <p className="mt-2 text-gray-500">
                Confirm this information before recording approved leave.
              </p>
            </div>

            <div className="grid gap-5">
              <ReviewCard
                icon={<FiUser />}
                title="Employee"
                value={
                  selectedEmployee
                    ? `${selectedEmployee.fullName} · ${selectedEmployee.employeeCode}`
                    : "—"
                }
              />

              <ReviewCard
                icon={<FiCalendar />}
                title="Leave Type"
                value={selectedLeaveType?.name || "—"}
              />

              <ReviewCard
                icon={<FiCalendar />}
                title="Duration"
                value={`${formData.startDate} → ${formData.endDate} · ${totalDays} day(s)`}
              />

              <ReviewCard
                icon={<FiFileText />}
                title="Reason"
                value={formData.reason}
              />

              <div className="rounded-2xl bg-green-50 border border-green-100 p-5 flex gap-3 text-green-800">
                <FiCheckCircle className="mt-0.5 shrink-0" size={19} />
                <p className="text-sm">
                  This creates an approved leave record directly and deducts
                  leave balance when the selected leave type requires it.
                </p>
              </div>
            </div>
          </>
        )}

        <div className="mt-10 flex justify-between border-t border-gray-100 pt-6">
          <button
            type="button"
            onClick={previousStep}
            disabled={currentStep === 0 || isSubmitting}
            className="px-6 py-3 rounded-xl border border-gray-200 disabled:opacity-50"
          >
            Previous
          </button>

          {currentStep === 3 ? (
            <button
              type="button"
              onClick={submitDirectLeave}
              disabled={isSubmitting}
              className="px-6 py-3 rounded-xl bg-primary-700 text-white flex items-center gap-2 disabled:opacity-60"
            >
              {isSubmitting ? <FiLoader className="animate-spin" /> : <FiSend />}
              Record Approved Leave
            </button>
          ) : (
            <button
              type="button"
              onClick={nextStep}
              className="px-6 py-3 rounded-xl bg-primary-700 text-white"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewCard({ icon, title, value }) {
  return (
    <div className="p-5 rounded-2xl border border-gray-100 bg-gray-50 flex gap-4">
      <div className="h-12 w-12 shrink-0 rounded-xl bg-primary-100 text-primary-800 flex items-center justify-center">
        {icon}
      </div>

      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="mt-1 font-semibold text-gray-900">{value || "—"}</p>
      </div>
    </div>
  );
}
